import React from "react";
import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { type PaginationError } from "../../utils/paginationErrors";

interface PaginationErrorBannerProps {
  error: PaginationError;
  isRetrying?: boolean;
  retryCount?: number;
  maxRetries?: number;
  onRetry: () => void;
  onDismiss: () => void;
}

export const PaginationErrorBanner: React.FC<PaginationErrorBannerProps> = ({
  error,
  isRetrying = false,
  retryCount = 0,
  maxRetries = 3,
  onRetry,
  onDismiss,
}) => {
  const canRetry = error.retryable && retryCount < maxRetries;
  
  const getRetryButtonText = () => {
    if (isRetrying) return "Retrying...";
    if (retryCount > 0) return `Retry (${retryCount}/${maxRetries})`;
    return "Retry";
  };

  const getErrorIcon = () => {
    switch (error.type) {
      case 'NETWORK_ERROR':
        return "🔌";
      case 'TIMEOUT_ERROR':
        return "⏱️";
      case 'GRAPHQL_ERROR':
        return "🛠️";
      default:
        return "⚠️";
    }
  };

  return (
    <Alert variant="destructive" className="mx-4 mb-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-lg">{getErrorIcon()}</span>
          <div>
            <p className="font-medium">Failed to load messages</p>
            <p className="text-sm text-muted-foreground">{error.message}</p>
          </div>
        </div>
        
        <div className="flex space-x-2">
          {canRetry && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={onRetry}
              disabled={isRetrying}
            >
              {getRetryButtonText()}
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="sm"
            onClick={onDismiss}
          >
            Dismiss
          </Button>
        </div>
      </div>
    </Alert>
  );
};